import React, { ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import AppContent from "../../components/AppContent";
import Downloading from "../../components/import-export/Downloading";
import Initial from "../../components/import-export/Initial";
import Saved from "../../components/import-export/Saved";
import Saving from "../../components/import-export/Saving";
import Uploading from "../../components/import-export/Uploading";
import ValidationFailure from "../../components/import-export/ValidationFailure";
import ValidationSuccess from "../../components/import-export/ValidationSuccess";
import VendorStationsExport from "../../components/VendorStationsExport";
import Vendor from "../../model/vendor";
import State from "../../State";
import intl from "../../utils/intl";
import * as actions from "./actions";
import StationTable from "./components/StationTable";
import messages from "./messages";
import StationsPageState from "./state";

const StationsWrapper = styled.div`
  padding-top: 20px;
`;

const StationsLayout = styled(AppContent)`
  padding: 30px;
`;

const VendorStationsExportWrapper = styled.div`
  padding: 30px;
  padding-top: 15px;
  background: #fff;
`;

function getContent(
  state: StationsPageState,
  uploadStations: (file: File) => void,
  completeStationsUpload: () => void,
  saveStations: (stationImportId: string) => void,
  downloadStations: () => void
): ReactNode {
  switch (state.importExportStationsState.type) {
    case "initial":
      return (
        <Initial
          upload={uploadStations}
          download={downloadStations}
          chooseOperationMessage={intl.formatMessage(messages.chooseOperation)}
        />
      );
    case "saved":
      return (
        <Saved
          messages={{
            saved: messages.stationsSaved,
            return: messages.returnToStations,
          }}
          completeUpload={completeStationsUpload}
          totalCount={state.importExportStationsState.totalCount}
        />
      );
    case "saving":
      return (
        <Saving
          messages={{
            saving: messages.savingStations,
          }}
        />
      );
    case "uploading":
      return (
        <Uploading
          messages={{
            uploading: messages.uploadingStations,
          }}
        />
      );
    case "upload-succeeded":
      return (
        <ValidationSuccess
          messages={{
            toBeSaved: messages.stationsToBeSaved,
            returnTo: messages.returnToStations,
            save: messages.save,
          }}
          validatedData={
            <StationTable stations={state.importExportStationsState.stations} />
          }
          importId={state.importExportStationsState.stationImportId}
          return={completeStationsUpload}
          save={saveStations}
        />
      );
    case "upload-failed":
      return (
        <ValidationFailure
          messages={{
            uploadFailed: messages.stationsUploadFailed,
            errors: messages.errors,
            returnTo: messages.returnToStations,
          }}
          errors={state.importExportStationsState.errors}
          complete={completeStationsUpload}
        />
      );
    case "downloading":
      return (
        <Downloading
          messages={{
            chooseOperation: messages.chooseOperation,
          }}
          downloading={true}
        />
      );
  }
}

const StationsPage: React.FC = () => {
  const dispatch = useDispatch();

  const uploadStations = (file: File): void => {
    dispatch(actions.uploadStations(file as File));
  };

  const completeStationsUpload = (): void => {
    dispatch(actions.completeStationsUpload());
  };

  const saveStations = (stationImportId: string): void => {
    dispatch(actions.saveStations(stationImportId));
  };

  const downloadStations = (): void => {
    dispatch(actions.downloadStations());
  };

  const exportVendorStations = (
    vendor: Vendor,
    vendorConfigurationName: string
  ): void => {
    dispatch(actions.exportVendorStations(vendor, vendorConfigurationName));
  };

  const state = useSelector<State, StationsPageState>(
    (state) => state.stations
  );

  return (
    <>
      <VendorStationsExportWrapper>
        <VendorStationsExport
          export={exportVendorStations}
          downloading={state.exportVendorStationsState.downloading}
        />
      </VendorStationsExportWrapper>
      <StationsWrapper>
        <StationsLayout>
          {getContent(
            state,
            uploadStations,
            completeStationsUpload,
            saveStations,
            downloadStations
          )}
        </StationsLayout>
      </StationsWrapper>
    </>
  );
};

export default StationsPage;
